// Need to use the React-specific entry point to import createApi
import type {EntityState} from '@reduxjs/toolkit'
import {createEntityAdapter, createSelector} from '@reduxjs/toolkit'
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {Device, DeviceSerializer} from "contracts/holotrak/device";
import {DeviceBase} from "contracts/holotrak/deviceBase";
import {DeviceListResponse, DeviceSensorListResponse} from "contracts/holotrak/deviceListResponse";
import {DeviceSubscription} from "contracts/holotrak/deviceSubscriptionListResponse";
import {MqttMessage} from "contracts/holotrak/mqttMessage";
import {SensorBase, SensorBaseSerializer} from "contracts/holotrak/sensorBase";
import {IReportableItem} from "contracts/IReportableItem";
import dayjs from "dayjs";
import {losantApi} from "redux/services/losant";
import {EGroupType} from "../../../contracts/EGroupType";
import {EDeviceFilterTypes} from "../../../contracts/IHolotrakItemFilter";
import {IOnMapDevices} from "../../../contracts/IOnMapDevices";
import {
    TAG_DEVICE,
    TAG_DEVICE_SENSOR,
    TAG_DEVICE_SENSOR_REPORT,
    TAG_DEVICE_SETTINGS,
    TAG_GEOFENCE_KEY,
    TAG_HISTORICAL_REPORT,
    TAG_INDOOR_ASSET
} from "../../../contracts/reduxResourceTags";
import {selectDeviceFilters, selectMapConfig, selectSearch, selectSelectionsForBatchType} from "../../slices/app";
import {RootState} from "../../store";
import {selectDeviceSubscriptionEntities} from "../losant/subscriptions";


export const devicesAdapter = createEntityAdapter<Device>({
    selectId: (device) => device.id,
});

export const deviceSensorAdapter = createEntityAdapter<SensorBase>({
    selectId: (sensor) => sensor.id,
});


const initialDeviceState = devicesAdapter.getInitialState();
const initialSensorState = deviceSensorAdapter.getInitialState();


// Define a service using a base URL and expected endpoints
export const SERVICE_REDUCER_PATH = "express";
export const expressApi = createApi({
    reducerPath: SERVICE_REDUCER_PATH,
    refetchOnReconnect: true,
    tagTypes: [TAG_DEVICE, TAG_DEVICE_SENSOR, TAG_DEVICE_SENSOR_REPORT, TAG_INDOOR_ASSET, TAG_GEOFENCE_KEY, TAG_HISTORICAL_REPORT],
    baseQuery: fetchBaseQuery(
        {
            baseUrl: process.env.REACT_APP_EXPRESS_SERVER,
            prepareHeaders: (headers, {getState}) => {
                const state = getState() as RootState;
                const token = btoa(state.auth?.user?.experienceGroups[0].id);
                if (token) {
                    headers.set("authorization", `Bearer ${token}`);
                }
                return headers;
            }
        },
    ),
    endpoints: (builder) => ({
        // Devices
        getDevices: builder.query<EntityState<Device>, void>({
            query: () => `devices`,
            providesTags: (result) => {
                if (result) {
                    return result.ids.map((id) => ({type: TAG_DEVICE, id}));
                }
                return [{type: TAG_DEVICE, id: 'LIST'}];
            },
            transformResponse: (response: DeviceListResponse) => {
                return devicesAdapter.addMany(
                    initialDeviceState,
                    response.devices
                );
            },
            async onCacheEntryAdded(
                arg,
                {updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState, dispatch}
            ) {
                let source: EventSource;
                const serverBase = process.env.REACT_APP_REALTIME_SERVER;
                const state = getState() as RootState;
                const organisationId = state.auth.user?.experienceGroups[0]?.id;

                try {
                    await cacheDataLoaded;

                    if (organisationId && 'EventSource' in window) {
                        const url = `${serverBase}/device/state?organizationId=${organisationId}`;
                        source = new EventSource(url);
                        const listener = (event: MessageEvent) => {
                            const data = JSON.parse(event.data);
                            // eslint-disable-next-line no-control-regex
                            const message = JSON.parse(data.message.replace(/[\u0000-\u0019]+/g, "-")) as MqttMessage;

                            const realTimeDevices = selectRealTimeDeviceIds(getState() as RootState);
                            if (!realTimeDevices.has(message.deviceId)) {
                                return;
                            }

                            updateCachedData((draft: EntityState<Device>) => {
                                let device = devicesAdapter.getSelectors().selectById(draft, message.deviceId);

                                if (!device) {
                                    return;
                                }

                                device = DeviceSerializer.parse({
                                    ...device,
                                    lastUpdated: dayjs(),
                                });
                                if (device && typeof device.updateState === 'function') {
                                    device.updateState(message);
                                    devicesAdapter.upsertOne(draft, device);
                                }
                            });
                            // invalidate a few tags
                            dispatch(
                                losantApi.util.invalidateTags([{
                                    type: TAG_DEVICE_SETTINGS,
                                    id: message.deviceId
                                }])
                            )
                        }
                        source.addEventListener('mqttMessage', listener, false);
                    }


                } catch {
                }
                await cacheEntryRemoved;
                if (source) {
                    source.close();
                }
            }
        }),
        // Sensors
        getDeviceSensors: builder.query<EntityState<SensorBase>, void>({
            query: () => `device-sensors`,
            providesTags: (result) => {
                if (result) {
                    return result.ids.map((id) => ({type: TAG_DEVICE_SENSOR, id}));
                }
                return [{type: TAG_DEVICE_SENSOR, id: 'LIST'}];
            },
            transformResponse: (response: DeviceSensorListResponse) => {
                return deviceSensorAdapter.addMany(
                    initialSensorState,
                    response.devices
                );
            },
            async onCacheEntryAdded(
                arg,
                {updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState, dispatch}
            ) {
                let source: EventSource;
                const serverBase = process.env.REACT_APP_REALTIME_SERVER;
                const state = getState() as RootState;
                const organisationId = state.auth.user?.experienceGroups[0]?.id;

                try {
                    await cacheDataLoaded;

                    /// TODO : Use single Universal Event Source for all trackers and Devices as well.
                    if (organisationId && 'EventSource' in window) {
                        const url = `${serverBase}/device/state?organizationId=${organisationId}`;
                        source = new EventSource(url);
                        const listener = (event: MessageEvent) => {
                            const data = JSON.parse(event.data);
                            // eslint-disable-next-line no-control-regex
                            const message = JSON.parse(data.message.replace(/[\u0000-\u0019]+/g, "-")) as MqttMessage;

                            updateCachedData((draft: EntityState<Device>) => {
                                let sensor = deviceSensorAdapter.getSelectors().selectById(draft, message.deviceId);
                                if (!sensor) {
                                    return;
                                }
                                sensor = SensorBaseSerializer.parse({
                                    ...sensor,
                                    lastUpdated: dayjs(),
                                });
                                if (sensor && typeof sensor.updateState === 'function') {
                                    sensor.updateState(message);
                                    deviceSensorAdapter.upsertOne(draft, sensor);
                                }
                            });
                        }
                        source.addEventListener('mqttMessage', listener, false);
                    }


                } catch {
                }
                await cacheEntryRemoved;
                if (source) {
                    source.close();
                }
            }
        }),

        syncDevices: builder.mutation<void, IReportableItem[]>({
            invalidatesTags: (result, error, devices) => {
                return [
                    ...devices.map(({id}) => ({type: TAG_DEVICE, id})),
                    ...devices.map(({id}) => ({type: TAG_INDOOR_ASSET, id})),
                    ...devices.map(({id}) => ({type: TAG_DEVICE_SENSOR, id})),
                    {type: TAG_DEVICE, id: 'LIST'},
                    {type: TAG_INDOOR_ASSET, id: 'LIST'},
                    {type: TAG_DEVICE_SENSOR, id: 'LIST'},
                    {type: TAG_DEVICE_SENSOR_REPORT, id: 'LIST'},
                ];
            },
            query: (devices) => {
                let deviceIds = devices.map(({id}) => id);
                return ({
                    url: `devices`,
                    method: "PUT",
                    body: {
                        devices: deviceIds,
                    },
                });
            },
        })
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetDevicesQuery,
    useGetDeviceSensorsQuery,
    useLazyGetDeviceSensorsQuery,
    useSyncDevicesMutation,
} = expressApi;

export const selectDevicesResult =
    expressApi.endpoints.getDevices.select();

export const selectDeviceSensorsResult = expressApi.endpoints.getDeviceSensors.select();

const selectDevicesData = createSelector(
    selectDevicesResult,
    (devicesResult) => devicesResult.data
);

const selectDeviceSensorData = createSelector(
    selectDeviceSensorsResult,
    (sensorsResult) => sensorsResult.data
);

export const {
    selectAll: selectPlainDevices,
    selectById: selectPlainDeviceById,
    selectIds: selectDeviceIds,
    selectTotal: selectTotalDevices,
} = devicesAdapter.getSelectors(
    (state: RootState) => selectDevicesData(state) ?? initialDeviceState
);

export const {
    selectAll: selectPlainDeviceSensors,
    selectById: selectPlainSensorById,
    selectIds: selectSensorIds,
    selectTotal: selectTotalSensors,
} = deviceSensorAdapter.getSelectors(
    (state: RootState) => selectDeviceSensorData(state) ?? initialSensorState
);

export const selectAllDevices = createSelector(
    selectPlainDevices,
    (devices) => {
        return devices.map(device => DeviceSerializer.parse(device)) || [];
    }
);

export const selectDeviceById = createSelector(
    selectPlainDeviceById,
    (deviceObject) => {
        return DeviceSerializer.parse(deviceObject);
    }
);

export const selectSensorById = createSelector(
    selectPlainSensorById,
    (sensorObject) => {
        return SensorBaseSerializer.parse(sensorObject);
    }
);

export const selectAllDeviceSensors = createSelector(
    selectPlainDeviceSensors,
    (sensors) => {
        return sensors.map(sensor => SensorBaseSerializer.parse(sensor)) || [];
    }
);

// RTK selector to filter devices by name
export const selectSearchedDevices = createSelector(
    selectAllDevices,
    (state: RootState) => {
        return selectSearch(state);
    },
    (devices, term) => {
        return devices.filter((device) => device?.name?.toLowerCase().includes(term.toLowerCase())) || [];
    }
);

export const selectSearchedSensors = createSelector(
    selectAllDeviceSensors,
    (state: RootState) => {
        return selectSearch(state);
    },
    (devices, term) => {
        return devices.filter((device) => device?.name?.toLowerCase().includes(term.toLowerCase())) || [];
    }
);

// RTK selector to get next and previous devices
const previousAndNextFinder = (devices: DeviceBase[], currentId: string) => {
    const currentDeviceIndex = devices.findIndex(device => device.id === currentId);
    const nextDevice = (currentDeviceIndex < devices.length) ? devices[currentDeviceIndex + 1] : null;
    const previousDevice = (currentDeviceIndex > 0) ? devices[currentDeviceIndex - 1] : null;

    return {
        nextDevice: nextDevice ?? null,
        previousDevice: previousDevice ?? null
    }
};
export const selectNextAndPreviousDevices = createSelector(
    [
        selectAllDevices,
        (_, id) => id
    ],
    previousAndNextFinder);

// RTK selector to get next and previous sensors
export const selectNextAndPreviousSensors = createSelector(
    [
        selectAllDeviceSensors,
        (_, id) => id
    ],
    previousAndNextFinder);


export const selectDevicesByName = createSelector(
    selectAllDevices,
    (_, filter: string) => filter.toLowerCase(),
    (devices: Device[], filter: string) => {
        if (!filter) {
            return devices;
        }
        return devices.filter((device) => device.name.toLowerCase().includes(filter)) || [];
    });

export const selectExternalSensorByName = createSelector(
    selectAllDeviceSensors,
    (_, filter: string) => filter.toLowerCase(),
    (sensors: SensorBase[], filter: string) => {
        if (!filter) {
            return sensors;
        }
        return sensors.filter((sensor) => sensor.name.toLowerCase().includes(filter)) || [];
    });

export const selectMapBoundDevices = createSelector(
    [
        selectAllDevices,
        selectMapConfig
    ],
    (devices, mapConfig) => {
        if (!mapConfig?.bounds) {
            return []; // Allows for better performance on accounts with high device count
        }

        return devices.filter((device) => {
            return device.latLng ? mapConfig.bounds.contains(device.latLng) : false;
        }) || [];
    }
);

export const selectMapBoundMovingDevices = createSelector(selectMapBoundDevices, (devices): IOnMapDevices => {
    return devices.reduce((onMapDevices, currentDevice) => {
        if (currentDevice.isMoving) {
            onMapDevices.moving.push(currentDevice);
        } else {
            onMapDevices.still.push(currentDevice);
        }

        return onMapDevices;
    }, {moving: [], still: []});
});

export const selectRealTimeDeviceIds = createSelector(
    [
        (state: RootState) => state.app.realTimeDevices,
        selectMapBoundDevices,
    ],
    (realTimeDevices, mapBoundDevices) => {
        return new Set([
            ...realTimeDevices,
            ...mapBoundDevices.map((device) => device.id),
        ]);
    }
);


export const selectFilteredDevices = createSelector(
    selectSearchedDevices,
    (state: RootState) => {
        return selectDeviceFilters(state); // replace with your own selector
    },
    (filteredDevices, filterConfig): Device[] => {
        if (filterConfig.filter === null) {
            return filteredDevices || [];
        }

        return filteredDevices.filter((device) => {

            switch (filterConfig.filterType) {
                case EDeviceFilterTypes.location:
                case EDeviceFilterTypes.status:
                    return device.status.toString() === filterConfig.filter?.toString();

                case EDeviceFilterTypes.event:
                default:
                    return true;
            }
        }) || [];
    }
);

// RTK selector to merge devices and their subscriptions
export const selectDevicesWithSubscriptions = createSelector(
    [selectAllDevices, selectDeviceSubscriptionEntities],
    (devices, subscriptions: DeviceSubscription[]) => {
        return devices.map((device) => {
            const subscription = subscriptions.find((subscription) => subscription.deviceId === device.id);

            if (subscription) {
                device.subscription = subscription;
            }

            return device;
        });
    }
);

// RTK Selector to get only the Chosen Devices
export const selectGroupMarkedDevices = createSelector(
    [
        selectFilteredDevices,
        (state) => selectSelectionsForBatchType(state, EGroupType.DEVICE)
    ],
    (devices, selections) => {
        let filteredDevices = (selections.length > 0)
            ? devices.filter((device) => selections.includes(device.id))
            : devices;

        return filteredDevices || [];
    }
);
